import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
    CalendarClock,
    Clock,
    MapPin,
    NotebookPen,
    Video,
    X,
} from "lucide-react";

import { Modal } from "../../../../shared/ui/modal";
import WhatsAppIcon from "../../../../shared/ui/icons/WhatsAppIcon";
import {
    ScheduledSession,
    ScheduledStatus,
    SessionUbication,
} from "../../domain/types";
import type { WhatsAppReminderAction } from "../../domain/whatsappReminder";

interface WeekSessionSheetProps {
    session: ScheduledSession;
    label: string;
    place?: string;
    /** `HH:MM – HH:MM` of the booking. */
    timeRange: string;
    /** The booking's day, already written out for the reader. */
    dateLabel: string;
    past: boolean;
    reminder?: WhatsAppReminderAction;
    onClose: () => void;
    onCancel: (session: ScheduledSession) => void;
    isCancelling?: boolean;
    onReschedule?: (session: ScheduledSession) => void;
    isRescheduling?: boolean;
}

const ACTION_BASE =
    "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left text-sm font-medium transition-colors disabled:opacity-50";

const ACTION_STYLE = `${ACTION_BASE} border-gray-200 text-content-primary hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5`;

/** Cancel is the only destructive action: red, so it is never hit by habit. */
const CANCEL_STYLE = `${ACTION_BASE} border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10`;

const ICON_STYLE = "shrink-0 text-brand-600 dark:text-brand-300";

/**
 * A booking of the week grid, opened by a click or tap on its block.
 *
 * A phone day column is ~45px wide: room for a name, not for the name and a
 * row of icons. So the block only names who it is, and opening it brings
 * everything else here — each action a full-width, labelled row a finger can
 * hit. Desktop opens the same sheet, so every screen offers the same actions.
 */
export default function WeekSessionSheet({
    session,
    label,
    place,
    timeRange,
    dateLabel,
    past,
    reminder,
    onClose,
    onCancel,
    isCancelling = false,
    onReschedule,
    isRescheduling = false,
}: WeekSessionSheetProps) {
    const { t } = useTranslation();
    const cancelled = session.scheduled_status === ScheduledStatus.CANCELLED;
    const joinLink =
        !cancelled &&
        session.ubication === SessionUbication.ONLINE &&
        session.meeting_link
            ? session.meeting_link
            : null;

    return (
        <Modal
            isOpen
            onClose={onClose}
            showCloseButton={false}
            ariaLabel={label}
            className="max-w-md"
        >
            <div className="flex flex-col gap-4 p-5">
                <header className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col gap-1">
                        <h2
                            className={`break-words text-lg font-bold text-content-primary ${
                                cancelled ? "line-through opacity-70" : ""
                            }`}
                        >
                            {label}
                        </h2>
                        <p className="flex items-center gap-1.5 text-sm text-content-secondary">
                            <Clock size={14} aria-hidden="true" className="shrink-0" />
                            <span>
                                {dateLabel} · {timeRange}
                            </span>
                        </p>
                        {place && (
                            <p className="flex items-center gap-1.5 text-sm text-content-secondary">
                                <MapPin size={14} aria-hidden="true" className="shrink-0" />
                                {place}
                            </p>
                        )}
                        {session.scheduled_status !== ScheduledStatus.SCHEDULED && (
                            <p className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
                                {t(`scheduler.statuses.${session.scheduled_status}`)}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("scheduler.calendar.closeSessionDetails")}
                        className="-mr-1 -mt-1 shrink-0 rounded-full p-2 text-content-tertiary hover:bg-gray-100 dark:hover:bg-white/10"
                    >
                        <X size={18} aria-hidden="true" />
                    </button>
                </header>

                <div className="flex flex-col gap-2">
                    {joinLink && (
                        <a
                            href={joinLink}
                            target="_blank"
                            rel="noreferrer"
                            onClick={onClose}
                            className={ACTION_STYLE}
                        >
                            <Video size={18} aria-hidden="true" className={ICON_STYLE} />
                            {t("scheduler.schedule.joinLink")}
                        </a>
                    )}
                    {reminder?.kind === "open" && (
                        <a
                            href={reminder.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                                reminder.onOpen?.();
                                onClose();
                            }}
                            className={ACTION_STYLE}
                        >
                            <WhatsAppIcon className={`size-[18px] ${ICON_STYLE}`} />
                            {t("scheduler.schedule.whatsappReminder")}
                        </a>
                    )}
                    {session.session_id && (
                        <Link
                            to={`/session/${session.session_id}`}
                            onClick={onClose}
                            className={ACTION_STYLE}
                        >
                            <NotebookPen size={18} aria-hidden="true" className={ICON_STYLE} />
                            {t("scheduler.schedule.viewSession")}
                        </Link>
                    )}
                    {/* A cancelled booking can be rescheduled whenever it
                        was: held after all. */}
                    {(cancelled || !past) && onReschedule && (
                        <button
                            type="button"
                            disabled={isRescheduling}
                            onClick={() => {
                                onClose();
                                onReschedule(session);
                            }}
                            className={ACTION_STYLE}
                        >
                            <CalendarClock size={18} aria-hidden="true" className={ICON_STYLE} />
                            {t("scheduler.schedule.reschedule")}
                        </button>
                    )}
                    {!cancelled && (
                        <button
                            type="button"
                            disabled={isCancelling}
                            onClick={() => {
                                onClose();
                                onCancel(session);
                            }}
                            className={CANCEL_STYLE}
                        >
                            <X size={18} aria-hidden="true" className="shrink-0" />
                            {t("scheduler.schedule.cancel")}
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
